import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BedDouble, Bath, Users, Star, MessageCircle, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { waLink } from "@/lib/content";

const VillaCard = ({ villa, index, config }) => {
  const [active, setActive] = useState(0);
  const images = villa.images?.length ? villa.images : [];
  const hasAirbnb = villa.airbnb_active && villa.airbnb_url;
  const wa = waLink(
    config.whatsapp_number,
    villa.whatsapp_message || `Hi BALIBIGVILLAS, I'm interested in booking ${villa.title}.`
  );

  const Destination = hasAirbnb ? "a" : "button";
  const destProps = hasAirbnb
    ? { href: villa.airbnb_url, target: "_blank", rel: "noopener noreferrer" }
    : { onClick: () => window.open(wa, "_blank", "noopener") };

  return (
    <Reveal delay={(index % 2) * 0.12}>
      <article
        data-testid={`villa-card-${index}`}
        className="group border border-stone-900/10 bg-white transition-shadow duration-700 hover:shadow-[0_30px_60px_-30px_rgba(28,25,23,0.25)]"
      >
        <Destination
          data-testid={`villa-airbnb-link-${index}`}
          {...destProps}
          className="img-frame relative block aspect-[4/3] w-full cursor-pointer overflow-hidden text-left"
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={active}
              src={images[active]}
              alt={villa.title}
              loading="lazy"
              initial={{ opacity: 0.4, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="h-full w-full object-cover"
            />
          </AnimatePresence>
          <span className="absolute left-4 top-4 bg-ivory/90 px-3 py-1.5 font-serif text-xs italic tracking-wide text-ink backdrop-blur-sm">
            N<sup>o</sup> {String(index + 1).padStart(2, "0")}
          </span>
          <span className="absolute right-4 top-4 flex items-center gap-1.5 bg-ink/70 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.15em] text-ivory backdrop-blur-sm">
            {hasAirbnb ? (
              <>
                View on Airbnb <ArrowUpRight size={11} />
              </>
            ) : (
              "Ask on WhatsApp"
            )}
          </span>
          {images.length > 1 && (
            <span className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
              {images.map((_, i) => (
                <span
                  key={i}
                  data-testid={`villa-gallery-dot-${index}-${i}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActive(i);
                  }}
                  className={`h-1.5 cursor-pointer rounded-full transition-all duration-500 ${
                    i === active ? "w-6 bg-gold" : "w-1.5 bg-ivory/70 hover:bg-ivory"
                  }`}
                />
              ))}
            </span>
          )}
        </Destination>

        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <Destination
              data-testid={`villa-title-link-${index}`}
              {...destProps}
              className="cursor-pointer text-left font-serif text-2xl font-normal leading-tight tracking-tight text-ink transition-colors duration-300 hover:text-gold-dark sm:text-[1.7rem]"
            >
              {villa.title}
            </Destination>
            {villa.rating && (
              <span
                data-testid={`villa-rating-${index}`}
                className="flex shrink-0 items-center gap-1.5 pt-1 text-sm text-stone-600"
              >
                <Star size={14} className="fill-gold text-gold" />
                {villa.rating}
                <span className="text-xs text-stone-400">({villa.reviews_count})</span>
              </span>
            )}
          </div>

          <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.25em] text-gold-dark">
            {villa.location}
          </p>
          <p className="mt-4 text-sm font-light leading-relaxed text-stone-600">
            {villa.description}
          </p>

          <div className="mt-6 flex items-center gap-6 border-t border-stone-900/10 pt-5 text-xs text-stone-600">
            <span className="flex items-center gap-2" data-testid={`villa-bedrooms-${index}`}>
              <BedDouble size={15} className="text-gold-dark" />
              {villa.bedrooms} Bedrooms
            </span>
            {villa.bathrooms && (
              <span className="flex items-center gap-2" data-testid={`villa-bathrooms-${index}`}>
                <Bath size={15} className="text-gold-dark" />
                {villa.bathrooms} Baths
              </span>
            )}
            <span className="flex items-center gap-2" data-testid={`villa-guests-${index}`}>
              <Users size={15} className="text-gold-dark" />
              Up to {villa.guests} Guests
            </span>
          </div>

          <a
            data-testid={`villa-whatsapp-cta-${index}`}
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            className="group/btn mt-7 flex items-center justify-center gap-3 bg-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink"
          >
            <MessageCircle size={15} className="transition-transform duration-500 group-hover/btn:-rotate-12" />
            Inquire About This Villa
          </a>
        </div>
      </article>
    </Reveal>
  );
};

export const Villas = ({ villas, config }) => (
  <section id="villas" className="bg-sand py-24 sm:py-32 lg:py-40" data-testid="villas-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="mb-16 grid gap-8 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-8">
          <p className="mb-5 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.3em] text-stone-500">
            <span className="h-px w-10 bg-gold" /> The Collection
          </p>
          <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Six Sanctuaries,
            <br />
            <span className="italic text-gold">One Standard.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-4">
          <p className="text-sm font-light leading-relaxed text-stone-600 sm:text-base">
            Six private residences crafted for total solitude, architectural
            elegance, and supreme comfort. Tap any image to inspect the
            verified Airbnb listing — or message us directly to book.
          </p>
        </Reveal>
      </div>

      <div className="grid gap-10 md:grid-cols-2 lg:gap-12">
        {villas.map((villa, i) => (
          <VillaCard key={villa.slug || i} villa={villa} index={i} config={config} />
        ))}
      </div>
    </div>
  </section>
);
